import { Button, Divider, Typography } from '@mui/material';
import { LP_REALISATION_LIST } from 'adminConstants/routes';
import Page from 'components/new/Page';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { numberToCurrencyString } from 'further-ui/utils';
import { Column, Row } from 'further-ui/components/Layout';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import StaticField from 'further-ui/components/StaticField';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import api from 'lib/trpcClient';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { ReversalFormValues } from './ReversalFormWrapper';

const CreateRealisationReversalPage: React.FC<{ vehicleId: string }> = ({
  vehicleId,
}) => {
  const { actionId } = useParams<{ actionId: string }>();
  const { transactionTypeOptions } = useTransactionTypeSelectorOptions();
  const { vehicle } = useLayoutContext();
  const { control, watch, handleSubmit, getValues } =
    useFormContext<ReversalFormValues>();
  const history = useHistory();
  const reversalDate = watch('reversalDate');

  const [realisation] = api.lpRealisation.get.useSuspenseQuery({
    realisationId: actionId ?? '',
    vehicleId,
  });

  const { data: reversalTransactions } =
    api.lpReversal.getRealisationTransactions.useQuery({
      vehicleId,
      actionId: actionId ?? '',
      reversalDate,
    });

  const reverseAction = api.lpReversal.reverse.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Realisation reversed successfully.',
      onSuccess: () => {
        history.push(LP_REALISATION_LIST);
      },
    }),
  );

  const onSubmit = () => {
    const data = getValues();
    reverseAction.mutate({
      ...data,
      vehicleId,
    });
  };

  const tcyTransactionCurrency = vehicle
    ? reversalTransactions?.find(
        (t) => t.transactionCurrency !== vehicle.baseCurrency,
      )?.transactionCurrency
    : undefined;

  const allocatorTransactionCurrency =
    tcyTransactionCurrency ?? realisation.currencyCode;

  const forceShowTcy =
    !!vehicle &&
    (realisation.currencyCode !== vehicle.baseCurrency ||
      !!tcyTransactionCurrency);

  return (
    <Page
      tabTitle="Reverse Realisation"
      spacing="lg"
      heading="Reverse Realisation"
      withHeadingDivider
      goBackAction={LP_REALISATION_LIST}
      actionBar={
        <Row fullWidth centered justify="space-between">
          <Button variant="outlined" onClick={() => history.goBack()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            loading={reverseAction.isPending}
          >
            Approve Reversal
          </Button>
        </Row>
      }
    >
      <Column spacing="lg">
        <FormField label="Reversal date" size="md">
          <Controller
            name="reversalDate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                fullWidth
                value={field.value ?? null}
                onChange={(date) => {
                  if (date) {
                    field.onChange(date);
                  }
                }}
                error={!!error}
                helperText={error?.message?.toString()}
              />
            )}
          />
        </FormField>

        <Divider />

        <Typography variant="h6">Original realisation</Typography>

        <Row spacing="lg">
          <StaticField label="Asset">{realisation.assetName ?? ''}</StaticField>
          <StaticField label="Amount">
            {numberToCurrencyString(
              realisation.totalRealisationAmountTcy ?? 0,
              { currency: realisation.currencyCode },
            )}
          </StaticField>
          <StaticField label="Ledger date">
            {realisation.ledgerDate?.toReadable() ?? ''}
          </StaticField>
        </Row>

        <Divider />

        <Typography variant="h6">Account transactions</Typography>

        {vehicle && (
          <TransactionAllocator
            readOnly
            transactions={reversalTransactions ?? []}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={allocatorTransactionCurrency}
            vehicle={{
              _id: vehicleId,
              baseCurrency: vehicle.baseCurrency,
              fxDataFeed: vehicle.fxDataFeed,
            }}
            fxRate={realisation.currencyRate ?? null}
            forceShowTcy={forceShowTcy}
          />
        )}
      </Column>
    </Page>
  );
};

export default CreateRealisationReversalPage;
